

import Vue from 'vue'
import Component from "vue-class-component";

import {namespace} from "vuex-class";
import {IOnOffHandlers} from "bonomo/dist";
import {EServiceProviderType, IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IErrors} from "@/model/IErrors";
import SPSettings from "@/components/serviceProvider/SPSettings.vue";
import SPSubscriptionSettings from "@/components/serviceProvider/SPSubscriptionSettings.vue";
import SPStripeDiscount from "@/components/serviceProvider/SPStripeDiscount.vue";
import SPLinkTracking from "@/components/serviceProvider/SPLinkTracking.vue";
import SPProfessionalServiceFields from "@/components/serviceProvider/SPProfessionalServiceFields.vue";
import SPAddress from "@/components/serviceProvider/SPAddress.vue";
import SPScope from "@/components/serviceProvider/SPScope.vue";
import SPInvoicing from "@/components/serviceProvider/SPInvoicing.vue";
import SPContact from "@/components/serviceProvider/SPContact.vue";
import SPTranslatableContent from "@/components/serviceProvider/SPTranslatableContent.vue";
import V2SPImageInput from "@/components/serviceProvider/v2/SpImageInput/V2SPImageInput.vue";
import {getFileImage} from "@/helpers/fileHelpers";
import {ITranslatedField} from "@/helpers/TranslationHelper";
import SPInfoCardInstructionList from "@/components/serviceProvider/SPInfoCardInstructionList.vue";
import {IServiceTag} from "@/model/Service/serviceInterfaces";

enum IMAGE_ENDPOINTS {
  logoImage = 'logoImage',
}

// todo: unify this with edit
interface IProviderForm extends IServiceProvider {
  tag_ids?: (string| number)[]
}

const serviceProviderModule = namespace("serviceProviderModule");
const serviceProviderServiceModule = namespace("serviceProviderServiceModule");

  @Component({
    components: {
      SPInfoCardInstructionList,
      V2SPImageInput,
      SPContact,
      SPInvoicing,
      SPAddress,
      SPScope,
      SPSettings,
      SPSubscriptionSettings,
      SPStripeDiscount,
      SPLinkTracking,
      SPProfessionalServiceFields,
      SPTranslatableContent,
    }
  })
  export default class ServiceProviderCreate extends Vue {

    @serviceProviderModule.Getter('errors')
    public errors!: IErrors;

    @serviceProviderModule.Action('fetchTags')
    protected fetchTags!: () => any;
    @serviceProviderModule.Getter('tags')
    protected availableTags!: IServiceTag[];
    public provider: IProviderForm = this.getEmptyProvider();

    public submitting: boolean = false;
    public showColorPicker: boolean = false;
    public submitted: boolean = false;
    public tagSearch: string = '';
    public tagIds: (IServiceTag | number | string)[] = [];

    public addressImageLogo: string|null = null;

    public fileImageLogo: File|null = null;

    public colorPickerSwatches: any = [
      [ '#4C8BEA', '#FFD735', ],
      [ '#C3C1B9', '#37DEE9', ],
      [ '#D31C00', '#4314FF', ],
      [ '#FD2CC2', '#FF9900', ],
      [ '#01C38B', '#00FFB5', ],
    ];
    private subscribes: IOnOffHandlers = {};

    created() {
      this.subscribes = this.$ons({
        'backend.call.OK.serviceProviderCreated': this.onCreated,
        'backend.call.KO.serviceProviderCreated': this.onCreateError,
      });
      this.fetchTags();
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }


    public get isProfessionalServices(): boolean {
      return this.provider.type == 'professional_services';
    }

    public get getPrimaryServiceDescription(): ITranslatedField {
      return this.provider ? this.provider.primary_service_description : {};
    }

    public get getPrimaryAboutUs(): ITranslatedField {
      return this.provider ? this.provider.about_us : {};
    }

    public get validateAllDescriptionsAreFilled(): boolean {
      return this.getEmptyDescriptionTextSections.length === 0;
    }

    public get getEmptyDescriptionTextSections(): string[] {
      const psDes: string[] = this.listITranslateFieldEmptyTexts(this.getPrimaryServiceDescription);
      const aboutUs: string[] = this.listITranslateFieldEmptyTexts(this.getPrimaryAboutUs);

      const result: string[] = [...psDes, ...aboutUs];
      return [...new Set(result)];
    }

    public listITranslateFieldEmptyTexts(textObject: ITranslatedField): string[] {
      const result: string[] = [];

      for (const property in textObject) {
        if (!textObject[property]) {
          result.push(property);
        }
      }

      return result;
    }

    public onLogoImageChange(newFile: File): Promise<any> {
      return getFileImage(newFile)
          .then((res) => {
            this.addressImageLogo = res;
            this.fileImageLogo = newFile;
          });
    }

    public onSave() {
      this.submitted = true;
      const spScope = this.$refs.spScope as any;
      if (spScope) {
        spScope.markSubmitted();
      }
      const scopeValid = !spScope || spScope.isValid;

      if (this.validateAllDescriptionsAreFilled && scopeValid && this.provider.channel_ids.length) {
        if (this.provider.tag_ids && this.provider.tag_ids.length > 0) {
          this.provider.tag_ids = this.provider.tag_ids.map((tag: IServiceTag | number | string) => {
            if (typeof tag === 'string') return tag as string;
            if (typeof tag === 'number') return tag as number;
            return tag.id as number;
          });
        }
        this.submitting = true;
        this.$store.dispatch('serviceProviderModule/createServiceProvider', this.provider);
      }
    }

    public onCancel() {
      this.$router.push({ name: 'serviceProviderList' });
    }

    private uploadImage(spId: number, fileToUpload: File, endpoint: IMAGE_ENDPOINTS): Promise<any> {
      return this.$store.dispatch('imageUploaderModule/uploadImage', {
        endpoint: 'serviceProviders/' + spId + '/' + endpoint,
        file: fileToUpload,
        translationPrefix: 'services.provider.errors',
      });
    }

    private uploadImageS3(spId: number, fileToUpload: File, endpoint: IMAGE_ENDPOINTS): Promise<any> {
      return this.$store.dispatch('imageUploaderModule/uploadServiceProviderImage', {
        spId,
        image: endpoint,
        file: fileToUpload,
        translationPrefix: 'services.provider.errors',
      });
    }

    private onCreated(data: any) {
      this.submitting = false;
      const spId = data && data.id;

      if (spId) {
        const uploads: Promise<any>[] = [];

        if (this.fileImageLogo) {
          uploads.push(this.uploadImageS3(spId, this.fileImageLogo, IMAGE_ENDPOINTS.logoImage));
        }

        if (uploads.length > 0) {
          Promise.all(uploads).then(() => {
            this.$uiBus.emit('success', this.$t('common.saved'));
            this.$router.push({ name: 'serviceProvider', params: { spId: String(spId) } });
          }, () => {
            // Even if image uploads fail, navigate to the created SP
            this.$uiBus.emit('success', this.$t('common.saved'));
            this.$router.push({ name: 'serviceProvider', params: { spId: String(spId) } });
          });
        } else {
          this.$uiBus.emit('success', this.$t('common.saved'));
          this.$router.push({ name: 'serviceProvider', params: { spId: String(spId) } });
        }
      } else {
        this.$uiBus.emit('success', this.$t('common.saved'));
        this.$router.push({ name: 'serviceProviderList' });
      }
    }

    private onCreateError() {
      this.submitting = false;
    }

    private getEmptyTranslatedField(): ITranslatedField {
      return {
        'es-ES': '',
        'ca-ES': '',
        'en-GB': '',
        'fr-FR': '',
        'pt-PT': '',
        'dk-DK': '',
        'de-DE': '',
      };
    }

    private getEmptyProvider(): IProviderForm {
      return {
        id: 0,
        active: '0',
        address_county: '',
        address_country_id: '',
        address_line_1: '',
        address_line_2: '',
        address_postcode: '',
        address_town_city: '',
        autoconfirm: '',
        categories: [],
        channel_ids: [],
        delivery_address: '',
        display_locker: false,
        email: '',
        extensions: [],
        has_item_tracking: '',
        invoicing_address: '',
        link: this.getEmptyTranslatedField(),
        phone_for_queries: '',
        name: '',
        payment_provider: '',
        phone_number: '',
        phone_number_mobile: '',
        send_communications: false,
        status: 'none',
        tax_id_number: '',
        tax_percentage: '',
        type: '' as any,
        uploads_enabled: false,
        use_only_restricted_cells: false,
        website: '',
        color: '#4C8BEA',
        qr_bizcocho: 0,
        qr_locker: 0,
        created_at: '',
        updated_at: '',
        deleted_at: '',
        about_us: this.getEmptyTranslatedField(),
        order_instructions: this.getEmptyTranslatedField(),
        primary_service_description: this.getEmptyTranslatedField(),
        infocard_cta_text: this.getEmptyTranslatedField(),
        logo_image_url: '',
        subscription_enabled: false,
        billing_anchor_day: null,
        billing_cutoff_day: null,
        billing_cycles_after_cutoff: null,
        subscription_proration_behavior: null,
        subscription_trial_days: null,
        subscription_trial_end_behavior: null,
        subscription_collection_method: null,
        subscription_days_until_due: null,
        subscription_payment_behavior: null,
        subscription_automatic_tax: null,
        stripe_discount_type: null,
        stripe_discount_value: null,
        service_price: 0,
        calculation_unit: 0,
        range_min: 1,
        range_max: 8,
        days_available: '',
        service_includes: this.getEmptyTranslatedField(),
        service_excludes: this.getEmptyTranslatedField(),
        instructions_1: this.getEmptyTranslatedField(),
        instructions_2: this.getEmptyTranslatedField(),
        instructions_3: this.getEmptyTranslatedField(),
        precheck_url: null,
        purchase_url: null,
        integration_data: null,
        limited_to: null,
        link_tracking_params: null,
        urgency_tag: this.getEmptyTranslatedField(),
        urgency_tag_type: '' as any,
        city_ids: [],
        tag_ids: [],
        tags: [],
        zipcodes: [],
        verticals: [],
        scope_country_id: null,
        is_demo: false,
        webview_hides_app_interface: false,
        service_tag_ids: [],
      } as IServiceProvider;
    }

    public get isInfoCard(): boolean {
      return this.provider.type == EServiceProviderType.infocard || this.provider.type == EServiceProviderType.infocard_sending;
    }
  }

